import axios from 'axios'
import Swal from 'sweetalert2'
import { getStandaloneIntl } from '../../../_metronic/i18n/standaloneIntl'
import { T, makeT } from '../profiles/dashboard/live/i18n'

export type RecurrenceScope = 'this' | 'future' | 'all'

/** Returned by GET /events/single/{id} as `recurrence`. */
export type RecurrenceInfo = {
  role: 'none' | 'master' | 'child'
  edit_future_count: number
  delete_future_count: number
  series_count: number
}

const swalClasses = {
  confirmButton: 'btn btn-primary',
  cancelButton: 'btn btn-light',
  input: 'text-start',
}

/**
 * SweetAlert lays radio options out in one row, which squeezes long (translated) labels into narrow
 * columns. Stack them instead.
 */
const stackRadios = (popup: HTMLElement) => {
  const radios = popup.querySelector<HTMLElement>('.swal2-radio')
  if (radios) {
    radios.style.flexDirection = 'column'
    radios.style.alignItems = 'flex-start'
    radios.style.textAlign = 'left'
    radios.style.gap = '0.5rem'
  }
}

/** Translator for the language selected in the application (the dialogs live outside React). */
const defaultT = (): T => makeT(getStandaloneIntl())

/**
 * Asks how far an edit of a recurring event should reach.
 * Resolves with the chosen scope, or null when the user cancels.
 *
 * Nothing is asked when the event is not recurring or there is nothing else the edit could reach:
 * the answer is then simply 'this'.
 *
 * `frequencyChanged` – a master's frequency cannot be applied to its upcoming occurrences (the API
 * refuses it), so only "this event" is offered and the user is told what happens instead.
 */
export const askEditScope = async (
  info: RecurrenceInfo | undefined,
  frequencyChanged = false,
  t: T = defaultT()
): Promise<RecurrenceScope | null> => {
  if (!info || info.role === 'none') {
    return 'this'
  }
  if (info.edit_future_count === 0 && !frequencyChanged) {
    return 'this'
  }
  const isMaster = info.role === 'master'
  const count = info.edit_future_count
  const options: Record<string, string> = {
    this: t(isMaster ? 'EVENTS.RECURRENCE.OPT_ONLY_MASTER' : 'EVENTS.RECURRENCE.OPT_ONLY_THIS'),
  }
  let note: string
  if (frequencyChanged) {
    note = t('EVENTS.RECURRENCE.NOTE_FREQUENCY')
  } else {
    options.future = t(
      isMaster ? 'EVENTS.RECURRENCE.OPT_FUTURE_MASTER' : 'EVENTS.RECURRENCE.OPT_FUTURE_CHILD',
      { count }
    )
    note = t('EVENTS.RECURRENCE.NOTE_EDIT')
  }
  const result = await Swal.fire({
    title: t('EVENTS.RECURRENCE.EDIT_TITLE'),
    html:
      `${t(isMaster ? 'EVENTS.RECURRENCE.MASTER_INTRO' : 'EVENTS.RECURRENCE.CHILD_INTRO')} ` +
      `${t('EVENTS.RECURRENCE.EDIT_QUESTION')}` +
      `<div class="text-muted fs-7 mt-3">${note}</div>`,
    icon: 'question',
    input: 'radio',
    inputOptions: options,
    inputValue: 'this',
    showCancelButton: true,
    confirmButtonText: t('EVENTS.RECURRENCE.SAVE'),
    cancelButtonText: t('EVENTS.RECURRENCE.CANCEL'),
    customClass: swalClasses,
    didOpen: stackRadios,
  })
  return result.isConfirmed ? (result.value as RecurrenceScope) : null
}

/**
 * Asks how much of a recurring series to delete (or just confirms for an ordinary event).
 * Resolves with the chosen scope, or null when the user cancels.
 */
export const askDeleteScope = async (
  info: RecurrenceInfo | undefined,
  t: T = defaultT()
): Promise<RecurrenceScope | null> => {
  if (!info || info.role === 'none') {
    const result = await Swal.fire({
      title: t('EVENTS.RECURRENCE.CONFIRM_TITLE'),
      html: t('EVENTS.RECURRENCE.CONFIRM_TEXT'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('EVENTS.RECURRENCE.DELETE'),
      cancelButtonText: t('EVENTS.RECURRENCE.CANCEL'),
      customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-primary' },
    })
    return result.isConfirmed ? 'this' : null
  }
  const isMaster = info.role === 'master'
  const options: Record<string, string> = isMaster
    ? {
        this: t('EVENTS.RECURRENCE.DEL_ONLY_MASTER'),
        future: t('EVENTS.RECURRENCE.DEL_FUTURE_MASTER', { count: info.delete_future_count }),
        all: t('EVENTS.RECURRENCE.DEL_ALL', { count: info.series_count }),
      }
    : {
        this: t('EVENTS.RECURRENCE.OPT_ONLY_THIS'),
        future: t('EVENTS.RECURRENCE.DEL_FUTURE_CHILD', { count: info.delete_future_count }),
        all: t('EVENTS.RECURRENCE.DEL_ALL', { count: info.series_count }),
      }
  const result = await Swal.fire({
    title: t('EVENTS.RECURRENCE.DELETE_TITLE'),
    html:
      `${t(isMaster ? 'EVENTS.RECURRENCE.MASTER_INTRO' : 'EVENTS.RECURRENCE.CHILD_INTRO')} ` +
      `${t('EVENTS.RECURRENCE.DELETE_QUESTION')}` +
      `<div class="text-muted fs-7 mt-3">${t('EVENTS.RECURRENCE.DEL_NOTE')}</div>`,
    icon: 'warning',
    input: 'radio',
    inputOptions: options,
    inputValue: 'this',
    showCancelButton: true,
    confirmButtonText: t('EVENTS.RECURRENCE.DELETE'),
    cancelButtonText: t('EVENTS.RECURRENCE.CANCEL'),
    customClass: { ...swalClasses, confirmButton: 'btn btn-danger' },
    didOpen: stackRadios,
  })
  return result.isConfirmed ? (result.value as RecurrenceScope) : null
}

/**
 * Deletes an event: loads its recurrence info, asks the user how much to delete, then deletes.
 * Resolves true when something was deleted, false when the user cancelled.
 */
export const deleteEventWithScope = async (id: string | number): Promise<boolean> => {
  const response = await axios.get('/events/single/' + id)
  const info: RecurrenceInfo | undefined = response?.data?.data?.recurrence
  const scope = await askDeleteScope(info)
  if (scope === null) {
    return false
  }
  await axios.delete('/events/save/' + id, { data: { recurrence_scope: scope } })
  return true
}
